import { useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAiAgentsSurface } from "@/pages/ai-agents-surface";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import {
  Sparkles,
  Plus,
  Bot,
  MoreVertical,
  Play,
  Pause,
  Trash2,
  Mail,
  Rocket,
  Activity,
  Droplets,
  Wrench,
  FileSearch,
  Loader2,
} from "lucide-react";
import type { AiAgent } from "@shared/schema";
import { LeadOutreachWizard } from "@/pages/platform/lead-outreach-wizard";

type LinkedDripCampaign = { id: string; name: string; aiAgentId: string | null };
type AgentWithCount = AiAgent & { runCount: number; linkedDripCampaign?: LinkedDripCampaign | null };

const IDENTITY_LABELS: Record<string, string> = {
  cold_mailbox: "Cold mailbox pool",
  connected_gmail: "Connected inbox",
};

const STATUS_STYLES: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  paused: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  archived: "bg-muted text-muted-foreground",
};

export default function PlatformAiAgents() {
  const { toast } = useToast();
  const { apiBase: AI_AGENTS_API, routeBase, Chrome, supportsEngineer } = useAiAgentsSurface();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AgentWithCount | null>(null);
  const [runWizardAgent, setRunWizardAgent] = useState<AgentWithCount | null>(null);
  const [name, setName] = useState("");
  const [identity, setIdentity] = useState<"cold_mailbox" | "connected_gmail">("cold_mailbox");
  const [agentType, setAgentType] = useState<"lead_outreach" | "engineer">("lead_outreach");

  const { data: agents = [], isLoading } = useQuery<AgentWithCount[]>({
    queryKey: [AI_AGENTS_API],
  });
  const displayedAgents = agents.filter((a) => a.agentType !== "engineer");

  const createMutation = useMutation({
    mutationFn: async () => {
      const isEngineer = agentType === "engineer";
      return apiRequest("POST", AI_AGENTS_API, {
        name: name.trim(),
        agentType,
        // Engineer agents never send outreach — no email identity.
        emailIdentityType: isEngineer ? null : identity,
        status: "active",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AI_AGENTS_API] });
      setCreateOpen(false);
      setName("");
      setIdentity("cold_mailbox");
      setAgentType("lead_outreach");
      toast({ title: "Agent created", description: "Your AI agent is ready to configure." });
    },
    onError: () => {
      toast({ title: "Could not create agent", variant: "destructive" });
    },
  });

  // Engineer agents: trigger a weekly self-diagnostics run on demand.
  const runDiagnosticsMutation = useMutation({
    mutationFn: async (agentId: string) =>
      apiRequest("POST", `/api/platform/ai-agents/${agentId}/run-engineer`, {
        windowDays: 7,
      }),
    onSuccess: () => {
      toast({
        title: "Diagnostics started",
        description: "Pooling telemetry and drafting the engineering brief. The report appears under Reports when ready.",
      });
    },
    onError: () => {
      toast({ title: "Could not start diagnostics", variant: "destructive" });
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      return apiRequest("PATCH", `${AI_AGENTS_API}/${id}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AI_AGENTS_API] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `${AI_AGENTS_API}/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AI_AGENTS_API] });
      setDeleteTarget(null);
      toast({ title: "Agent deleted" });
    },
    onError: () => {
      toast({ title: "Could not delete agent", variant: "destructive" });
      setDeleteTarget(null);
    },
  });

  return (
    <Chrome>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary/10 p-2.5">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-page-title">
                AI Agents
              </h1>
              <p className="text-sm text-muted-foreground max-w-2xl">
                Synthetic sales reps that bulk-generate prospects by city, state and
                business type, then enroll them into cold outreach campaigns — automatically.
              </p>
            </div>
          </div>
          <Button onClick={() => setCreateOpen(true)} data-testid="button-new-agent">
            <Plus className="h-4 w-4 mr-2" />
            New Agent
          </Button>
        </div>

        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-44 rounded-lg border bg-muted/30 animate-pulse" />
            ))}
          </div>
        ) : displayedAgents.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Bot className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium">No agents yet</h3>
              <p className="text-sm text-muted-foreground max-w-sm mt-1">
                Create your first AI agent to start automating cold lead generation and outreach.
              </p>
              <Button className="mt-4" onClick={() => setCreateOpen(true)} data-testid="button-new-agent-empty">
                <Plus className="h-4 w-4 mr-2" />
                Create an agent
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {displayedAgents.map((agent) => (
              <Card key={agent.id} data-testid={`card-agent-${agent.id}`} className="flex flex-col">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="rounded-full bg-primary/10 p-2">
                        <Bot className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-base" data-testid={`text-agent-name-${agent.id}`}>
                          {agent.name}
                        </CardTitle>
                        <Badge
                          variant="secondary"
                          className={`mt-1 ${STATUS_STYLES[agent.status] ?? ""}`}
                          data-testid={`status-agent-${agent.id}`}
                        >
                          {agent.status}
                        </Badge>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`button-agent-menu-${agent.id}`}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {agent.status === "active" ? (
                          <DropdownMenuItem
                            onClick={() => statusMutation.mutate({ id: agent.id, status: "paused" })}
                            data-testid={`button-pause-${agent.id}`}
                          >
                            <Pause className="h-4 w-4 mr-2" /> Pause
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem
                            onClick={() => statusMutation.mutate({ id: agent.id, status: "active" })}
                            data-testid={`button-activate-${agent.id}`}
                          >
                            <Play className="h-4 w-4 mr-2" /> Activate
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setDeleteTarget(agent)}
                          data-testid={`button-delete-${agent.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                {agent.agentType === "engineer" ? (
                  <CardContent className="flex flex-col gap-3 flex-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Wrench className="h-4 w-4" />
                      Weekly self-diagnostics
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Pools cross-workspace errors, performance and UX-friction telemetry into a
                      human-reviewed engineering brief with an exportable error log. Runs Monday
                      mornings; no changes are ever auto-applied.
                    </p>
                    <div className="mt-auto pt-2 grid grid-cols-1 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled={agent.status !== "active" || runDiagnosticsMutation.isPending}
                        onClick={() => runDiagnosticsMutation.mutate(agent.id)}
                        data-testid={`button-run-diagnostics-${agent.id}`}
                      >
                        {runDiagnosticsMutation.isPending ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Wrench className="h-4 w-4 mr-2" />
                        )}
                        Run diagnostics now
                      </Button>
                      <Link
                        href={`/platform/engineer-reports?agentId=${agent.id}`}
                        className="w-full"
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full"
                          data-testid={`button-view-reports-${agent.id}`}
                        >
                          <FileSearch className="h-4 w-4 mr-2" />
                          View reports
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                ) : (
                  <CardContent className="flex flex-col gap-3 flex-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      {agent.emailIdentityType
                        ? IDENTITY_LABELS[agent.emailIdentityType]
                        : "No sending identity"}
                    </div>
                    <Link
                      href={`${routeBase}/${agent.id}`}
                      className="text-sm text-muted-foreground hover:text-foreground hover:underline"
                      data-testid={`link-runs-${agent.id}`}
                    >
                      {agent.runCount} {agent.runCount === 1 ? "run" : "runs"} — view tracking
                    </Link>
                    {agent.linkedDripCampaign ? (
                      <div
                        className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted rounded px-2 py-1"
                        data-testid={`drip-link-${agent.id}`}
                      >
                        <Droplets className="h-3.5 w-3.5 text-blue-500" />
                        <span>Drip:</span>
                        <a
                          href="/platform/communications"
                          className="font-medium text-foreground hover:underline truncate"
                          title={agent.linkedDripCampaign.name}
                        >
                          {agent.linkedDripCampaign.name}
                        </a>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`drip-link-${agent.id}`}>
                        <Droplets className="h-3.5 w-3.5 opacity-30" />
                        <span>No drip campaign linked</span>
                      </div>
                    )}
                    <div className="mt-auto pt-2 grid grid-cols-1 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled={agent.status !== "active"}
                        onClick={() => { setRunWizardAgent(agent); }}
                        data-testid={`button-new-run-${agent.id}`}
                      >
                        <Rocket className="h-4 w-4 mr-2" />
                        New Lead Outreach Run
                      </Button>
                      <Link href={`${routeBase}/${agent.id}`} className="w-full">
                        <Button variant="ghost" size="sm" className="w-full" data-testid={`button-view-runs-${agent.id}`}>
                          <Activity className="h-4 w-4 mr-2" />
                          View runs &amp; ROI
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create agent dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New AI Agent</DialogTitle>
            <DialogDescription>
              {agentType === "engineer"
                ? "Give your diagnostics agent a name. It pools telemetry and drafts a weekly engineering brief."
                : "Give your agent a name and choose how it will send outreach email."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="agent-name">Agent name</Label>
              <Input
                id="agent-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={
                  agentType === "engineer" ? "e.g. Ada — Weekly Diagnostics" : "e.g. Riley — Lead Outreach"
                }
                data-testid="input-agent-name"
              />
            </div>
            {supportsEngineer && (
              <div className="space-y-2">
                <Label>Agent type</Label>
                <Select
                  value={agentType}
                  onValueChange={(v) => setAgentType(v as typeof agentType)}
                >
                  <SelectTrigger data-testid="select-agent-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lead_outreach">Lead Outreach — generate & contact prospects</SelectItem>
                    <SelectItem value="engineer">Engineer — weekly self-diagnostics</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {agentType !== "engineer" && (
              <div className="space-y-2">
                <Label>Sending identity</Label>
                <Select value={identity} onValueChange={(v) => setIdentity(v as typeof identity)}>
                  <SelectTrigger data-testid="select-agent-identity">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cold_mailbox">Cold mailbox pool (recommended)</SelectItem>
                    <SelectItem value="connected_gmail">Connected inbox</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Cold mailbox pool rotates dedicated sending domains for high volume.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!name.trim() || createMutation.isPending}
              data-testid="button-create-agent"
            >
              {createMutation.isPending ? "Creating…" : "Create agent"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the agent and its run history. Prospects already generated
              are kept. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Lead Outreach wizard */}
      {runWizardAgent && (
        <LeadOutreachWizard
          agent={runWizardAgent}
          open={!!runWizardAgent}
          onOpenChange={(o) => !o && setRunWizardAgent(null)}
          initialCampaignId={(runWizardAgent as AgentWithCount).linkedDripCampaign?.id ?? undefined}
        />
      )}
    </Chrome>
  );
}
